import { FC, ReactNode, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { useLayoutEditorForm } from '../../use/editor/useLayoutEditorForm';
import { useLayoutEditorEntity } from '../../use/editor/useLayoutEditorEntity';
import { useLayoutEditorCompositeField } from '../../components/CompositeField/useLayoutEditorCompositeField';
import type { LayoutEditorContextType, ColumnModification } from './LayoutEditorContext';
import { LayoutEditorContext } from './LayoutEditorContext';
import { createCollectionStore } from '../../store';
import { useSet } from '../../use/data/useSet';
import { UserContext } from '../UserContext';
import type { ArTableType, TypeId, ArColumnDefinition } from '#types/System.Artifacts.types';
import type { VisibilityTypeId } from '#types/System.types';

const tableTypesStore = createCollectionStore<ArTableType>('tableTypes');

function applyColumnMod(col: ArColumnDefinition, mod: ColumnModification): ArColumnDefinition {
    return {
        ...col,
        visibilityTypes: mod.visibilityTypes !== undefined
            ? (mod.visibilityTypes.length > 0 ? mod.visibilityTypes : undefined)
            : col.visibilityTypes,
        kitMemberHiddenTypes: mod.kitMemberHiddenTypes !== undefined
            ? (mod.kitMemberHiddenTypes.length > 0 ? mod.kitMemberHiddenTypes : undefined)
            : col.kitMemberHiddenTypes,
        groupMemberHiddenTypes: mod.groupMemberHiddenTypes !== undefined
            ? (mod.groupMemberHiddenTypes.length > 0 ? mod.groupMemberHiddenTypes : undefined)
            : col.groupMemberHiddenTypes,
    };
}

type LayoutEditorProviderProps = {
    type: 'form' | 'entity' | 'compositeField';
    children: ReactNode;
};

/**
 * Provider component that handles both form and entity editing contexts.
 * Uses type discrimination to ensure type safety and proper context usage.
 * Also loads ArTable type definitions for table element configuration.
 *
 * The ArTable types are resolved BEFORE the three editor hooks, because
 * `useLayoutEditorForm` needs them to run the save-time derived-value gate — the
 * pane and the gate must judge cycles against the same `formulaSequence`s, and
 * one listener here is the only place that holds them. `tableTypesLoading` goes
 * with them: `[]` while loading and `[]` for a type-less corpus are the same list,
 * and only that flag tells the gate which one it is holding.
 *
 * @example
 * ```tsx
 * // For forms
 * <LayoutEditorProvider type="form">
 *   <YourComponent />
 * </LayoutEditorProvider>
 * 
 * // For entities
 * <LayoutEditorProvider type="entity">
 *   <YourComponent />
 * </LayoutEditorProvider>
 * ```
 */
export const LayoutEditorProvider: FC<LayoutEditorProviderProps> = (props) => {
    const { isLoggedIn, loading: authLoading } = useContext(UserContext);
    const { set } = useSet();

    const isActive = isLoggedIn && !authLoading;

    useEffect(() => {
        if (!isActive) return;
        tableTypesStore.connect();
        return () => tableTypesStore.disconnect();
    }, [isActive]);

    const tableTypesSnap = useSnapshot(tableTypesStore.state);
    const rawTableTypes = useMemo<(ArTableType & { docId: string })[]>(() => {
        if (!isActive) return [];
        const result: (ArTableType & { docId: string })[] = [];
        for (const id of tableTypesSnap.docIds) {
            const entry = tableTypesSnap.docs.get(id);
            if (entry) result.push({ ...(entry.data as ArTableType), docId: id });
        }
        return result;
    }, [isActive, tableTypesSnap.docIds, tableTypesSnap.docs]);
    /**
     * "Not available" rather than "still fetching": while `isActive` is false the
     * list above is `[]` for a reason that has nothing to do with the corpus, and
     * calling that loaded would let the derive save gate judge cycles against no
     * tables at all and refuse a correct configuration.
     */
    const tableTypesLoading = !isActive || tableTypesSnap.loading;

    const [tableTypeModifications, setTableTypeModifications] = useState<Map<string, Map<TypeId, ColumnModification>>>(
        () => new Map()
    );

    const updateColumnModification = useCallback((
        tableId: string,
        columnTypeId: TypeId,
        patch: Partial<ColumnModification>,
    ) => {
        setTableTypeModifications(prev => {
            const next = new Map(prev);
            const tableMods = new Map(next.get(tableId) ?? []);
            tableMods.set(columnTypeId, { ...tableMods.get(columnTypeId), ...patch });
            next.set(tableId, tableMods);
            return next;
        });
    }, []);

    const updateTableColumnVisibility = useCallback((
        tableId: string,
        columnTypeId: TypeId,
        visibilityTypes: VisibilityTypeId[],
    ) => updateColumnModification(tableId, columnTypeId, { visibilityTypes }), [updateColumnModification]);

    const updateTableColumnKitMemberHidden = useCallback((
        tableId: string,
        columnTypeId: TypeId,
        kitMemberHiddenTypes: VisibilityTypeId[],
    ) => updateColumnModification(tableId, columnTypeId, { kitMemberHiddenTypes }), [updateColumnModification]);

    const updateTableColumnGroupMemberHidden = useCallback((
        tableId: string,
        columnTypeId: TypeId,
        groupMemberHiddenTypes: VisibilityTypeId[],
    ) => updateColumnModification(tableId, columnTypeId, { groupMemberHiddenTypes }), [updateColumnModification]);

    const saveTableTypeModifications = useCallback(async () => {
        const promises: Promise<void>[] = [];

        for (const [tableId, columnMods] of tableTypeModifications) {
            const tableType = rawTableTypes.find(t => t.docId === tableId);
            if (!tableType) continue;

            const updatedColumns = tableType.columns.map(col => {
                const mod = columnMods.get(col.typeId);
                if (!mod) return col;
                return applyColumnMod(col, mod);
            });

            promises.push(set('tableTypes', tableId, { columns: updatedColumns }));
        }

        await Promise.all(promises);
        setTableTypeModifications(new Map());
    }, [tableTypeModifications, rawTableTypes, set]);

    const tableTypes = useMemo(() => {
        if (tableTypeModifications.size === 0) return rawTableTypes;

        return rawTableTypes.map(tableType => {
            const columnMods = tableTypeModifications.get(tableType.docId);
            if (!columnMods) return tableType;

            return {
                ...tableType,
                columns: tableType.columns.map(col => {
                    const mod = columnMods.get(col.typeId);
                    if (!mod) return col;
                    return applyColumnMod(col, mod);
                }),
            };
        });
    }, [rawTableTypes, tableTypeModifications]);

    const hasTableTypeModifications = tableTypeModifications.size > 0;

    const formEditor = useLayoutEditorForm({ tableTypes, tableTypesLoading });
    const entityEditor = useLayoutEditorEntity();
    const compositeFieldEditor = useLayoutEditorCompositeField();

    const tableTypesData = {
        tableTypes,
        tableTypesLoading,
        tableTypeModifications,
        hasTableTypeModifications,
        updateTableColumnVisibility,
        updateTableColumnKitMemberHidden,
        updateTableColumnGroupMemberHidden,
        saveTableTypeModifications,
    };

    const contextValue: LayoutEditorContextType = props.type === 'form'
        ? { ...formEditor, type: 'form', ...tableTypesData }
        : props.type === 'entity'
            ? { ...entityEditor, type: 'entity', ...tableTypesData }
            : { ...compositeFieldEditor, type: 'compositeField', ...tableTypesData };

    return (
        <LayoutEditorContext.Provider value={contextValue}>
            {props.children}
        </LayoutEditorContext.Provider>
    );
};

 